import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { VenueData, TicketGroupData } from '../types';

// Validation schema for the ticket form
const createValidationSchema = (
  isTicketFree: boolean,
  isGroupTicket: boolean,
  hasMultipleVenues: boolean,
  hasMultipleTicketGroups: boolean,
  isOfferActive: boolean,
  ticketOrder: any,
) =>
  Yup.object({
    ticketType: Yup.string().required('Ticket name is required'),
    price: isTicketFree
      ? Yup.number().optional()
      : Yup.number()
          .required('Ticket amount is required')
          .min(0, 'Price must be 0 or greater'),

    groupTicketCount: isGroupTicket
      ? Yup.number()
          .required('Number of people is required')
          .min(2, 'Group capacity must be at least 2 attendees')
      : Yup.number().optional(),
    venueId: Yup.string().test(
      'venue-required',
      'Venue selection is required',
      value => !hasMultipleVenues || !!value
    ),
    ticketGroupId: Yup.string().test(
      'group-required',
      'Ticket group selection is required',
      value => !hasMultipleTicketGroups || !!value
    ),
    maxCount: isGroupTicket
      ? Yup.number()
          .required('Number of group tickets available is required')
          .typeError('Available tickets must be a number')
          .min(0, 'Quantity must be 0 or greater')
      : Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' ? null : value
      )
      .typeError('Available tickets must be a number')
      .min(0, 'Quantity must be 0 or greater'),
    previousAmount: isOfferActive
      ? Yup.number()
          .required('Previous amount is required when offer is active')
          .min(0, 'Previous amount must be 0 or greater')
      : Yup.number().optional(),
    ticketOrder :Yup.number().min(1,'Must be 1 or greater')
  });

type NewTicketModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: any) => void;
  isLoading?: boolean;
  ticketKind: 'single' | 'group';
  hasMultipleVenues?: boolean;
  venues?: VenueData[];
  hasMultipleTicketGroups?: boolean;
  ticketGroups?: TicketGroupData[];
  isTicketFree?: boolean;
  editingTicket?: any;
  type?: boolean;
  ticketOrder?: any;
};

const NewTicketModal: React.FC<NewTicketModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  ticketKind,
  hasMultipleVenues = false,
  venues = [],
  hasMultipleTicketGroups = false,
  ticketGroups = [],
  isTicketFree = false,
  editingTicket,
  type,
  ticketOrder
}) => {

  // State for offer checkbox
  const [isOfferActive, setIsOfferActive] = useState(editingTicket?.isOfferActive || false);

  // Initialize formik with validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ticketType: editingTicket?.name || editingTicket?.ticketType || '',
      description: editingTicket?.description || '',
      price: editingTicket?.price !== undefined && editingTicket?.price !== null ? editingTicket.price.toString() : '',
      maxCount: editingTicket?.totalTickets !== undefined && editingTicket?.totalTickets !== null
        ? editingTicket.totalTickets.toString()
        : editingTicket?.maxCount !== undefined && editingTicket?.maxCount !== null
        ? editingTicket.maxCount.toString()
        : '',
      isGroupTicket: editingTicket?.isGroupTicket || ticketKind === 'group',
      groupTicketCount: editingTicket?.groupTicketCount !== undefined && editingTicket?.groupTicketCount !== null ? editingTicket.groupTicketCount.toString() : '',
      venueId: editingTicket?.venueId || '',
      ticketGroupId: editingTicket?.ticketGroupId || '',
      isOfferActive: editingTicket?.isOfferActive || false,
      previousAmount: editingTicket?.previousAmount !== undefined && editingTicket?.previousAmount !== null ? editingTicket.previousAmount.toString() : '',
      ticketOrder: editingTicket?.ticketOrder?.toString() || '1',
    },
    validationSchema: createValidationSchema(
      isTicketFree,
      ticketKind === 'group',
      hasMultipleVenues,
      hasMultipleTicketGroups,
      isOfferActive,
      ticketOrder
    ),
    onSubmit: (values) => {
      const rawMax = values.maxCount !== "" && values.maxCount !== null && values.maxCount !== undefined ? parseInt(values.maxCount, 10) : null;
      const formData = {
        ...values,
        ticketType: values.ticketType,
        name: values.ticketType,
        maxCount: isNaN(rawMax as any) ? null : rawMax,
        price: isTicketFree ? 0 : (parseFloat(values.price) || 0),
        groupTicketCount: ticketKind === 'group' ? (parseInt(values.groupTicketCount, 10) || undefined) : undefined,
        venueId: hasMultipleVenues ? values.venueId : undefined,
        ticketGroupId: hasMultipleTicketGroups ? values.ticketGroupId : undefined,
        id: editingTicket?.id,
        isOfferActive: isOfferActive,
        previousAmount: isOfferActive ? (parseFloat(values.previousAmount) || undefined) : undefined,
        ticketOrder: parseInt(values.ticketOrder, 10) || 1,
      };
      onSubmit(formData);
    }
  });


  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
      setIsOfferActive(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update isOfferActive when editingTicket changes
  React.useEffect(() => {
    if (editingTicket) {
      setIsOfferActive(editingTicket.isOfferActive || false);
    }
  }, [editingTicket]);

  return (
    <>
    <style>{`
      .custom-glass-modal .modal-content {
        background: rgba(20, 20, 32, 0.75) !important;
        backdrop-filter: blur(24px) !important;
        -webkit-backdrop-filter: blur(24px) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        border-radius: 20px !important;
        box-shadow: 0 32px 64px rgba(0, 0, 0, 0.6) !important;
      }
      /* Mobile-only: more breathing room between ticket fields */
      @media (max-width: 768px) {
        .new-ticket-modal-body {
          gap: 28px !important;
          padding: 20px 16px !important;
        }
        .new-ticket-modal-body .new-ticket-field {
          gap: 10px !important;
        }
        .new-ticket-modal-footer {
          flex-direction: column-reverse !important;
          gap: 10px !important;
          padding: 0 16px 20px 16px !important;
        }
        .new-ticket-modal-footer .v3-btn-primary,
        .new-ticket-modal-footer .v3-btn-secondary {
          width: 100% !important;
        }
      }
    `}</style>
    <Modal modalClassName="custom-glass-modal quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <h4 className="mb-0" style={{ color: '#fff' }}>
          {(type === true || ticketKind === 'group') ? 'Group Ticket' : 'Single Ticket'}
        </h4>
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody className="px-4 py-4 new-ticket-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Ticket Name Field */}
          <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="v3-label">Ticket Name <span className="text-danger">*</span></label>
            <input className="v3-input"
              name="ticketType"
              type="text"
              placeholder="Enter Ticket Name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.ticketType}
            />
            {validation.touched.ticketType && validation.errors.ticketType ? (
              <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                { validation.errors.ticketType as string}
              </div>
            ) : null}
          </div>

          {/* Venue Selection Field - Only show if hasMultipleVenues is true */}
          {hasMultipleVenues && venues.length > 0 && (
            <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="v3-label">Select Venue<span className="text-danger"> *</span></label>
              <select className="v3-input"
                name="venueId"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.venueId}
              >
                <option value="">Select a venue</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </select>
              {validation.touched.venueId && validation.errors.venueId ? (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.venueId as string}
                </div>
              ) : null}
            </div>
          )}

          {/* Ticket Group Selection Field - Only show if hasMultipleTicketGroups is true */}
          {hasMultipleTicketGroups && ticketGroups.length > 0 && (
            <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="v3-label">Select Ticket Group<span className="text-danger"> *</span></label>
              <select className="v3-input"
                name="ticketGroupId"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.ticketGroupId}
              >
                <option value="">Select a ticket group</option>
                {ticketGroups.map((ticketGroup) => (
                  <option key={ticketGroup.id} value={ticketGroup.id}>
                    {ticketGroup.name}
                  </option>
                ))}
              </select>
              {validation.touched.ticketGroupId && validation.errors.ticketGroupId ? (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  {validation.errors.ticketGroupId as string}
                </div>
              ) : null}
            </div>
          )}

          {/* Ticket Amount Field - Only for Paid Tickets */}
          {!isTicketFree && (
            <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="v3-label">
                {ticketKind === 'group'
                  ? 'Price per Group'
                  : isOfferActive
                  ? 'Offer Amount'
                  : 'Ticket Amount'}{' '}
                <span className="text-danger">*</span>
              </label>
              <input className="v3-input"
                name="price"
                type="text"
                placeholder="Enter Ticket Amount"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.price}
              />
              {validation.touched.price && validation.errors.price ? (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.price as string}
                </div>
              ) : null}
            </div>
          )}

          {/* Free Ticket Indicator */}
          {isTicketFree && (
            <div style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(52,211,153,0.2)', fontSize: 13 }}>
              <i className="fa-solid fa-gift me-2"></i>
              <strong>Free Ticket:</strong> This ticket will be free for attendees.
            </div>
          )}

          {/* Previous Amount Field - Only show when Is Offer is active */}
          {!isTicketFree && isOfferActive && (
            <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="v3-label">
                Previous Amount <span className="text-danger">*</span>
              </label>
              <input className="v3-input"
                name="previousAmount"
                type="text"
                placeholder="Enter Previous Amount"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.previousAmount}
              />
              {validation.touched.previousAmount && validation.errors.previousAmount ? (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.previousAmount as string}
                </div>
              ) : null}
              <small style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                This will be shown as the original price before the discount
              </small>
            </div>
          )}

          {/* Max Count Field */}
          <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="v3-label">
              {ticketKind === 'group' ? (
                <>Number of Group Tickets Available <span className="text-danger">*</span></>
              ) : (
                'Ticket Quantity'
              )}
            </label>
            <input className="v3-input"
              name="maxCount"
              type="text"
              placeholder="Enter 0 for unlimited availability."
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.maxCount}
            />
            {validation.touched.maxCount && validation.errors.maxCount ? (
              <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                { validation.errors.maxCount as string}
              </div>
            ) : null}
            <small style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
              Enter the total number of tickets available for sale. Enter 0 for unlimited availability.
            </small>
          </div>

          {/* Group Ticket Count Field - Only show if group ticket is selected */}
          {ticketKind === 'group' && (
            <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="v3-label">Attendee per Group <span className="text-danger">*</span></label>
              <input className="v3-input"
                name="groupTicketCount"
                type="number"
                placeholder="Enter the number of attendees per group"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.groupTicketCount}
              />
              {validation.touched.groupTicketCount && validation.errors.groupTicketCount ? (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.groupTicketCount as string}
                </div>
              ) : null}
            </div>
          )}
          {/* Ticket Order Field */}
          <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="v3-label">Ticket Order</label>
            <input className="v3-input"
              name="ticketOrder"
              type="text"
              placeholder="Enter Ticket Order"
              value={validation.values.ticketOrder}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.ticketOrder && validation.errors.ticketOrder && (
              <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.ticketOrder as string}
              </div>
            )}
          </div>
          {/* Ticket Description Field */}
          <div className="new-ticket-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="v3-label">Ticket Description</label>
            <textarea className="v3-input"
              name="description"
              placeholder="Enter Ticket Description"
              value={validation.values.description}
              rows={4}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.description && validation.errors.description && (
              <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  { validation.errors.description as string}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="new-ticket-modal-footer" style={{ borderTop: 'none', padding: '0 24px 24px 24px', display: 'flex', gap: 12, justifyContent: 'stretch' }}>
          <button
            type="button"
            className="v3-btn-secondary"
            style={{ padding: "14px 20px", fontSize: 14, fontWeight: 700, borderRadius: 14, flex: 1 }}
            onClick={toggle}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="v3-btn-primary shadow-glow"
            style={{ padding: "14px 20px", fontSize: 16, fontWeight: 700, borderRadius: 14, flex: 1.4 }}
            type="submit"
            disabled={isLoading}
          >
            {isLoading
              ? editingTicket ? "Saving..." : "Creating..."
              : editingTicket ? "Save Changes" : "Create Ticket"}
          </button>
        </ModalFooter>
      </Form>
    </Modal>
    </>
  );
};

export default NewTicketModal;
